import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import '../styles/auth.css';
import {
  clearRegisterDraft,
  submitRegistration,
} from '../services/registerService';
import { loginUser, saveUserSession } from '../services/authService';
import { termsFooter, termsSections } from '../constants/legalContent';
import { getPersonNameError, sanitizePersonNameInput } from '../utils/personName';

const UserIcon = () => (
  <svg className="auth-field-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 20c0-4 3.5-7 8-7s8 3 8 7" />
  </svg>
);

const PhoneIcon = () => (
  <svg className="auth-field-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6 4h4l2 3h4a2 2 0 012 2v11H4V6a2 2 0 012-2z" />
  </svg>
);

const MailIcon = () => (
  <svg className="auth-field-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3 7l9 6 9-6" />
  </svg>
);

const CalendarIcon = () => (
  <svg className="auth-field-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="4" y="5" width="16" height="15" rx="2" />
    <path d="M8 3v4M16 3v4M4 10h16" />
  </svg>
);

const LockIcon = () => (
  <svg className="auth-field-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V8a4 4 0 118 0v3" />
  </svg>
);

const EyeIcon = () => (
  <svg className="auth-field-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const CheckStepIcon = () => (
  <svg className="register-step-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M5 12l4 4 10-10" />
  </svg>
);

const wizardSteps = [
  { id: 1, label: 'Sobre mi' },
  { id: 2, label: 'Términos y condiciones' },
];

const emptyRegisterForm = () => ({
  nombre: '',
  apellido: '',
  telefono: '',
  email: '',
  fechaNacimiento: '',
  genero: '',
  password: '',
  confirmPassword: '',
  sinHijos: true,
  hijos: [],
  aceptaTerminos: false,
});

const RegisterWizard = ({ step, setStep }) => {
  const history = useHistory();
  const [formData, setFormData] = useState(emptyRegisterForm);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [allowFormInput, setAllowFormInput] = useState(false);

  useEffect(() => {
    clearRegisterDraft();
    setFormData(emptyRegisterForm());
    setAllowFormInput(false);

    const timers = [50, 150, 400].map((delay) =>
      window.setTimeout(() => {
        setFormData(emptyRegisterForm());
      }, delay)
    );

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, []);

  const unlockFormInput = () => {
    setAllowFormInput(true);
  };

  const updateField = (name, value) => {
    const nextValue =
      name === 'nombre' || name === 'apellido' ? sanitizePersonNameInput(value) : value;
    setFormData((current) => ({ ...current, [name]: nextValue }));
  };

  const validateStepOne = () => {
    if (!formData.nombre.trim() || !formData.apellido.trim()) {
      return 'Nombre y apellido son obligatorios.';
    }
    const nameError = getPersonNameError(formData.nombre, formData.apellido);
    if (nameError) {
      return nameError;
    }
    if (!formData.telefono.trim() || !formData.email.trim()) {
      return 'Teléfono y correo son obligatorios.';
    }
    if (!formData.fechaNacimiento || !formData.genero) {
      return 'Completa tu fecha de nacimiento y género.';
    }
    if (!formData.password || formData.password !== formData.confirmPassword) {
      return 'Las contraseñas deben coincidir.';
    }
    return '';
  };

  const handleNext = () => {
    setErrorMessage('');
    if (step === 1) {
      const error = validateStepOne();
      if (error) {
        setErrorMessage(error);
        return;
      }
      setStep(2);
    }
  };

  const handleBack = () => {
    setErrorMessage('');
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleComplete = () => {
    setErrorMessage('');
    if (!formData.aceptaTerminos) {
      setErrorMessage('Debes aceptar los términos y condiciones.');
      return;
    }

    setIsSubmitting(true);
    submitRegistration({
      ...formData,
      sinHijos: true,
      hijos: [],
    })
      .then(() => {
        setStep(3);
      })
      .catch((error) => {
        setErrorMessage(error.message || 'No se pudo completar el registro.');
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handlePostRegisterLogin = () => {
    setErrorMessage('');
    setIsLoggingIn(true);

    loginUser({
      email: formData.email,
      password: formData.password,
    })
      .then((user) => {
        saveUserSession(user, false);
        history.push('/perfil');
      })
      .catch((error) => {
        setErrorMessage(
          error.message ||
            'No se pudo iniciar sesión automáticamente. Usa Iniciar sesión e ingresa tus datos.'
        );
      })
      .finally(() => {
        setIsLoggingIn(false);
      });
  };

  if (step === 3) {
    return (
      <div className="auth-root">
        <section className="auth-section">
          <div className="register-success-card">
            <div className="register-success-icon" aria-hidden="true">
              ✓
            </div>
            <h1 className="register-success-title">¡Registro completado con éxito!</h1>
            <p className="register-success-text">
              Tu cuenta fue creada correctamente. Ya puedes iniciar sesión y disfrutar de la experiencia en
              Beyond Limits Foundation.
            </p>
            {errorMessage ? <p className="auth-error">{errorMessage}</p> : null}
            <button
              type="button"
              className="auth-submit register-success-btn"
              onClick={handlePostRegisterLogin}
              disabled={isLoggingIn}
            >
              {isLoggingIn ? 'Iniciando sesión...' : 'Iniciar sesión'}
            </button>
            {errorMessage ? (
              <p className="register-success-text">
                <Link to="/iniciar-sesion" className="auth-inline-link">
                  Ir al formulario de inicio de sesión
                </Link>
              </p>
            ) : null}
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="auth-root">
      <section className="auth-section">
        <div className="register-wizard-shell">
          <aside className="register-wizard-sidebar">
            <h1 className="register-wizard-heading">
              {step === 1 ? '¡Hazte parte de nosotros!' : 'Bienvenido,'}
            </h1>
            <p className="register-wizard-intro">
              {step === 1
                ? 'Regístrate en el portal y comparte tu experiencia sobre los colegios.'
                : 'Ingresa los datos para registrarte'}
            </p>

            <ol className="register-stepper">
              {wizardSteps.map((item) => {
                const isComplete = step > item.id;
                const isActive = step === item.id;
                return (
                  <li
                    key={item.id}
                    className={`register-stepper-item${
                      isComplete ? ' register-stepper-item--complete' : ''
                    }${isActive ? ' register-stepper-item--active' : ''}`}
                  >
                    <span className="register-stepper-marker" aria-hidden="true">
                      {isComplete ? <CheckStepIcon /> : null}
                    </span>
                    <span>{item.label}</span>
                  </li>
                );
              })}
            </ol>
          </aside>

          <div className="register-wizard-panel">
            {step === 1 ? (
              <form
                className="register-wizard-form"
                autoComplete="off"
                onSubmit={(event) => {
                  event.preventDefault();
                  handleNext();
                }}
              >
                {/* Señuelos para que el navegador no rellene los campos reales. */}
                <div className="register-autofill-decoy" aria-hidden="true">
                  <input type="text" name="username" tabIndex={-1} autoComplete="username" />
                  <input type="password" name="password" tabIndex={-1} autoComplete="current-password" />
                </div>
                <div className="register-form-row">
                  <label className="auth-field">
                    <span className="auth-field-control">
                      <UserIcon />
                      <input
                        type="text"
                        name="blf_reg_nombre"
                        placeholder="Nombre"
                        value={formData.nombre}
                        onChange={(event) => updateField('nombre', event.target.value)}
                        onFocus={unlockFormInput}
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck={false}
                        readOnly={!allowFormInput}
                        required
                      />
                    </span>
                  </label>
                  <label className="auth-field">
                    <span className="auth-field-control">
                      <UserIcon />
                      <input
                        type="text"
                        name="blf_reg_apellido"
                        placeholder="Apellido"
                        value={formData.apellido}
                        onChange={(event) => updateField('apellido', event.target.value)}
                        onFocus={unlockFormInput}
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck={false}
                        readOnly={!allowFormInput}
                        required
                      />
                    </span>
                  </label>
                </div>

                <div className="register-form-row">
                  <label className="auth-field">
                    <span className="auth-field-control">
                      <PhoneIcon />
                      <input
                        type="tel"
                        name="blf_reg_telefono"
                        placeholder="Número telefónico"
                        value={formData.telefono}
                        onChange={(event) => updateField('telefono', event.target.value)}
                        onFocus={unlockFormInput}
                        autoComplete="off"
                        readOnly={!allowFormInput}
                        required
                      />
                    </span>
                  </label>
                  <label className="auth-field">
                    <span className="auth-field-control">
                      <MailIcon />
                      <input
                        type="email"
                        name="blf_reg_email"
                        placeholder="Correo electrónico"
                        value={formData.email}
                        onChange={(event) => updateField('email', event.target.value)}
                        onFocus={unlockFormInput}
                        autoComplete="off"
                        readOnly={!allowFormInput}
                        required
                      />
                    </span>
                  </label>
                </div>

                <div className="register-form-row register-form-row--birth">
                  <label className="auth-field">
                    <span className="auth-field-control auth-field-control--highlight">
                      <CalendarIcon />
                      <input
                        type="date"
                        name="blf_reg_fecha"
                        placeholder="Fecha de nacimiento"
                        value={formData.fechaNacimiento}
                        onChange={(event) => updateField('fechaNacimiento', event.target.value)}
                        onFocus={unlockFormInput}
                        autoComplete="off"
                        required
                      />
                    </span>
                  </label>
                  <div className="register-gender-group">
                    <button
                      type="button"
                      className={`register-choice-btn${
                        formData.genero === 'hombre' ? ' register-choice-btn--active' : ''
                      }`}
                      onClick={() => updateField('genero', 'hombre')}
                    >
                      Masculino
                    </button>
                    <button
                      type="button"
                      className={`register-choice-btn${
                        formData.genero === 'mujer' ? ' register-choice-btn--active' : ''
                      }`}
                      onClick={() => updateField('genero', 'mujer')}
                    >
                      Femenino
                    </button>
                  </div>
                </div>

                <label className="auth-field">
                  <span className="auth-field-control">
                    <LockIcon />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="blf_reg_password"
                      placeholder="Contraseña"
                      value={formData.password}
                      onChange={(event) => updateField('password', event.target.value)}
                      onFocus={unlockFormInput}
                      autoComplete="new-password"
                      readOnly={!allowFormInput}
                      required
                    />
                    <button
                      type="button"
                      className="auth-password-toggle"
                      onClick={() => setShowPassword((value) => !value)}
                      aria-label="Mostrar u ocultar contraseña"
                    >
                      <EyeIcon />
                    </button>
                  </span>
                </label>

                <label className="auth-field">
                  <span className="auth-field-control">
                    <LockIcon />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="blf_reg_confirm_password"
                      placeholder="Confirmar contraseña"
                      value={formData.confirmPassword}
                      onChange={(event) => updateField('confirmPassword', event.target.value)}
                      onFocus={unlockFormInput}
                      autoComplete="new-password"
                      readOnly={!allowFormInput}
                      required
                    />
                    <button
                      type="button"
                      className="auth-password-toggle"
                      onClick={() => setShowConfirmPassword((value) => !value)}
                      aria-label="Mostrar u ocultar confirmación"
                    >
                      <EyeIcon />
                    </button>
                  </span>
                </label>

                {errorMessage ? <p className="register-error">{errorMessage}</p> : null}

                <div className="register-wizard-actions">
                  <button
                    type="button"
                    className="register-btn-secondary"
                    onClick={() => history.push('/iniciar-sesion')}
                  >
                    Ya tengo cuenta
                  </button>
                  <button type="submit" className="register-btn-primary">
                    Siguiente &gt;
                  </button>
                </div>
              </form>
            ) : null}

            {step === 2 ? (
              <div className="register-wizard-form">
                <h2 className="register-section-title">Términos y Condiciones</h2>
                <div className="register-terms-copy">
                  {termsSections.map((section, index) => (
                    <article key={section.title} className="register-terms-section">
                      <h3 className="register-terms-section-title">
                        <span>{index + 1}. </span>
                        {section.title}
                      </h3>
                      {section.body ? (
                        <p className="register-terms-paragraph">{section.body}</p>
                      ) : null}
                      {section.items && section.items.length ? (
                        <ul className="register-terms-list">
                          {section.items.map((item) => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                      ) : null}
                      {section.subsections
                        ? section.subsections.map((subsection) => (
                            <div key={subsection.title} className="register-terms-subsection">
                              <h4 className="register-terms-subtitle">{subsection.title}</h4>
                              {subsection.body ? (
                                <p className="register-terms-paragraph">{subsection.body}</p>
                              ) : null}
                              {subsection.items && subsection.items.length ? (
                                <ul className="register-terms-list">
                                  {subsection.items.map((item) => (
                                    <li key={item}>{item}</li>
                                  ))}
                                </ul>
                              ) : null}
                            </div>
                          ))
                        : null}
                    </article>
                  ))}
                  {termsFooter ? <p className="register-terms-footer">{termsFooter}</p> : null}
                </div>

                <label className="register-terms-check">
                  <input
                    type="checkbox"
                    checked={formData.aceptaTerminos}
                    onChange={(event) => updateField('aceptaTerminos', event.target.checked)}
                  />
                  <span>Acepto los términos y condiciones</span>
                </label>

                {errorMessage ? <p className="register-error">{errorMessage}</p> : null}

                <div className="register-wizard-actions">
                  <button type="button" className="register-btn-secondary" onClick={handleBack}>
                    &lt; Regresar
                  </button>
                  <button
                    type="button"
                    className="register-btn-primary"
                    onClick={handleComplete}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Completando...' : 'Completar registro >'}
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
};

const CreateAccountPage = () => {
  const [step, setStep] = useState(1);
  return <RegisterWizard step={step} setStep={setStep} />;
};

export default CreateAccountPage;
